import { MaterialTuple } from 'app/constants/Materials';
import { CallEndedRoute, LessonBookingRoute, MeetingBookingRoute } from 'app/constants/Routes';
import { ParticipantType } from 'app/models/rtc/constants';
import { LocationDescriptorObject } from 'app/services/history';
import { IDeviceManagerBehaviorOptions } from 'app/services/rtc/DeviceManager/DeviceManagerState';
import { DeviceType } from 'app/services/rtc/DeviceManager/DeviceState';
import { EntityStringId } from 'app/types/entity';
import { ONE_MINUTE_MS } from 'app/utils/date';
import { LocationDescriptor } from 'history';
import { Brand } from 'app/constants/schemas';
import * as io from 'io-ts';
import qs from 'query-string';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { WebRTCFeatures } from 'app/services/rtc/constants';
import { Set } from 'immutable';
import { LessonBookingOutcome } from 'app/models/rtc/booking/lessons/outcome';

export type BookingId = EntityStringId;

// Query params
export const enum BookingQueryParam {
  BookingId   = 'callBookingId',
  BookingType = 'callBookingType',
  BookingHostKey = 'hostKey',
}

// supported types
export const enum BookingType {
  Lesson              = 'lesson',
  Meeting             = 'meeting',
  TeacherRegistration = 'teacherRegistration',
  TestCall            = 'testCall',
}

export const BookingTypeCodec: io.Type<BookingType> = io.union([
  io.literal(BookingType.Lesson),
  io.literal(BookingType.Meeting),
  io.literal(BookingType.TeacherRegistration),
  io.literal(BookingType.TestCall),
]);


// Two parameters that can be used to uniquely distinguish bookings.
export interface IBookingTuple {
  bookingId: BookingId;
  bookingType: BookingType;
  hostKey?: string;
}

/**
 *  Reasons for a booking being cleared
 */
export enum CallEndReason {
  Idle = 'idle',
  Left = 'left',
}

export enum CallEndedPageParam {
  Reason = 'reason',
}

// How long before a certain meeting type will be auto-disconnected due to being idle
export const LESSON_IDLE_DISCONNECT_TIME = ONE_MINUTE_MS * 10;
export const MEETING_IDLE_DISCONNECT_TIME = ONE_MINUTE_MS * 30;
export const TEST_CALL_IDLE_DISCONNECT_TIME = 1000 * 30;

export class BookingTypeSetting<T extends BookingType> {
  public readonly bookingType: T;
  public readonly requiredFeatures: Set<WebRTCFeatures>;
  public readonly defaultParticipantType: ParticipantType;
  public readonly showSidebar: boolean;
  /**
   * Custom UI controls connecting and disconnecting, don't
   * automatically reconnect or provide global UI for reconnecting
   */
  public readonly controlsConnection: boolean;
  /**
   * Behavior options for device setup.
   */
  public readonly deviceSetupBehavior: IDeviceManagerBehaviorOptions;
  /**
   * Notifies the user when they try to go offsite.
   */
  public readonly blockOffsiteNavigation: boolean;
  /**
   * Whether to show the X button to close the call
   */
  public readonly allowClose: boolean;
  /**
   * Allows the Notifier to play sounds when peers join/leave.
   */
  public readonly enableSoundOnPresenceChange: boolean;
  public readonly allowSyncNavigation: boolean;
  /**
   * Autoloads the booking if found in the URI.
   */
  public readonly autoLoad: boolean;
  public readonly showChatSettings: boolean;
  /**
   * Gets the location that the user should be sent after the call ends.
   */
  public readonly getPostCallLocation?: (id: BookingId, disconnectReason: CallEndReason) => LocationDescriptor;
  /**
   * Gets the location that the user should be sent when reconnecting to a call.
   */
  public readonly getReconnectLocation?: (id: BookingId) => LocationDescriptor;

  /**
   * Time in milliseconds that a call participant can be idle before the booking is automatically cleared
   */
  public readonly idleDisconnectTime: number;

  public constructor(args: Pick<BookingTypeSetting<T>, keyof BookingTypeSetting<T>>) {
    this.requiredFeatures            = args.requiredFeatures;
    this.allowClose                  = args.allowClose;
    this.allowSyncNavigation         = args.allowSyncNavigation;
    this.autoLoad                    = args.autoLoad;
    this.blockOffsiteNavigation      = args.blockOffsiteNavigation;
    this.bookingType                 = args.bookingType;
    this.controlsConnection          = args.controlsConnection;
    this.defaultParticipantType      = args.defaultParticipantType;
    this.enableSoundOnPresenceChange = args.enableSoundOnPresenceChange;
    this.getPostCallLocation         = args.getPostCallLocation;
    this.getReconnectLocation        = args.getReconnectLocation;
    this.deviceSetupBehavior         = args.deviceSetupBehavior;
    this.showSidebar                 = args.showSidebar;
    this.showChatSettings            = args.showChatSettings;
    this.idleDisconnectTime          = args.idleDisconnectTime;
  }
}

// Create a mapped type for the booking types to their settings.
export const BookingTypeSettings: {
  readonly [K in BookingType]: BookingTypeSetting<K>;
} = Object.freeze({
  [BookingType.Lesson]: new BookingTypeSetting({
    requiredFeatures: Set([
      WebRTCFeatures.UserMedia,
      WebRTCFeatures.PeerConnection,
    ]),
    allowClose: false,
    allowSyncNavigation: true,
    autoLoad: true,
    blockOffsiteNavigation: true,
    bookingType: BookingType.Lesson,
    controlsConnection: false,
    defaultParticipantType: ParticipantType.Participant,
    getPostCallLocation: (id: BookingId, disconnectReason: CallEndReason = CallEndReason.Left) => {
      const params = qs.stringify({ [CallEndedPageParam.Reason]: disconnectReason });
      return `${CallEndedRoute.getPathname({ type: BookingType.Lesson, id })}?${params}`;
    },
    getReconnectLocation: (id: BookingId) => LessonBookingRoute.getPathname({ id }),
    deviceSetupBehavior: {
      [DeviceType.Audio]: {
        manual: false,
        required: true,
      },
      [DeviceType.Video]: {
        manual: false,
        required: false,
      },
    },
    enableSoundOnPresenceChange: true,
    idleDisconnectTime: LESSON_IDLE_DISCONNECT_TIME,
    showChatSettings: true,
    showSidebar: true,
  }),
  [BookingType.Meeting]: new BookingTypeSetting({
    requiredFeatures: Set([
      WebRTCFeatures.UserMedia,
      WebRTCFeatures.PeerConnection,
      WebRTCFeatures.UnifiedPlan,
    ]),
    allowClose: true,
    allowSyncNavigation: true,
    autoLoad: true,
    blockOffsiteNavigation: true,
    bookingType: BookingType.Meeting,
    controlsConnection: false,
    defaultParticipantType: ParticipantType.Participant,
    getPostCallLocation: (id: BookingId, disconnectReason: CallEndReason = CallEndReason.Left) => {
      const params = qs.stringify({ [CallEndedPageParam.Reason]: disconnectReason });
      return `${CallEndedRoute.getPathname({ type: BookingType.Meeting, id })}?${params}`;
    },
    getReconnectLocation: (id: BookingId) => MeetingBookingRoute.getPathname({ id }),
    deviceSetupBehavior: {
      [DeviceType.Audio]: {
        manual: false,
        required: true,
      },
      [DeviceType.Video]: {
        manual: false,
        required: false,
      },
    },
    enableSoundOnPresenceChange: true,
    idleDisconnectTime: MEETING_IDLE_DISCONNECT_TIME,
    showChatSettings: true,
    showSidebar: true,
  }),
  [BookingType.TeacherRegistration]: new BookingTypeSetting({
    requiredFeatures: Set([
      WebRTCFeatures.UserMedia,
      WebRTCFeatures.PeerConnection,
      WebRTCFeatures.UnifiedPlan,
    ]),
    allowClose: false,
    allowSyncNavigation: false,
    autoLoad: false,
    blockOffsiteNavigation: false,
    bookingType: BookingType.TeacherRegistration,
    controlsConnection: true,
    defaultParticipantType: ParticipantType.Observer,
    deviceSetupBehavior: {
      [DeviceType.Audio]: {
        manual: true,
        required: true,
      },
      [DeviceType.Video]: {
        manual: true,
        required: true,
      },
    },
    enableSoundOnPresenceChange: false,
    idleDisconnectTime: Infinity,
    showChatSettings: false,
    showSidebar: false,
  }),
  [BookingType.TestCall]: new BookingTypeSetting({
    requiredFeatures: Set([
      WebRTCFeatures.UserMedia,
      WebRTCFeatures.PeerConnection,
    ]),
    allowClose: false,
    allowSyncNavigation: false,
    autoLoad: false,
    blockOffsiteNavigation: false,
    bookingType: BookingType.TestCall,
    controlsConnection: true,
    defaultParticipantType: ParticipantType.Observer,
    deviceSetupBehavior: {
      [DeviceType.Audio]: {
        manual: true,
        required: true,
      },
      [DeviceType.Video]: {
        manual: true,
        required: false,
      },
    },
    enableSoundOnPresenceChange: false,
    idleDisconnectTime: TEST_CALL_IDLE_DISCONNECT_TIME,
    showChatSettings: false,
    showSidebar: false,
  }),
});

export interface IBookingTime {
  end: number;
  start: number;
}

/**
 * JoinParams common for every type of call, for now empty
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IBaseJoinParams {
}

interface IBookingStateBase {
  readonly bookingId: BookingId;
  readonly participantType: ParticipantType;
  readonly rtcRoom: EntityStringId | null;
  readonly joinParams: IBaseJoinParams;
  readonly displayName?: string;
}

export interface IMeetingJoinParams extends IBaseJoinParams {
  /**
   * Allows to upgrade participant to a host
   */
  host_key?: string;
}

export interface IMeetingBookingState extends IBookingStateBase {
  bookingType: BookingType.Meeting;
  joinParams: IMeetingJoinParams;
}

export interface ILessonBookingNavigation {
  readonly tag: {
    readonly category: EntityStringId | null;
    readonly course: EntityStringId | null;
    readonly lesson: EntityStringId | null;
  };
  readonly url: LocationDescriptorObject;
}

export interface ILessonBookingState extends IBookingStateBase {
  readonly bookingType: BookingType.Lesson;
  readonly lessonRole: LessonBookingRole;
  readonly brand?: Brand;
  readonly materials: MaterialTuple[];
  readonly navigation: ILessonBookingNavigation;
  readonly displayName: string;
  /**
   * Describes the outcome of a lesson booking. This is intended to only
   * be used to derive an outcome for a call and should not be used outside
   * of that context.
   */
  readonly outcome: LessonBookingOutcome | null;
  readonly studentId: EntityStringId;
  readonly teacherId: EntityStringId;
  /**
   * LessonId -> BookingId
   */
  readonly tieUps: {
    [index: string]: BookingId;
  };
  readonly time: IBookingTime;
}

export interface IBookingStateOther extends IBookingStateBase {
  bookingType: BookingType.TeacherRegistration | BookingType.TestCall;
}

export type BookingState =
  | IMeetingBookingState
  | ILessonBookingState
  | IBookingStateOther;

export type JoinParams =
  | IMeetingJoinParams
  | IBaseJoinParams;

export type IdleDisconnectableBookingType = BookingType.Lesson | BookingType.Meeting | BookingType.TestCall;

export const isIdleDisconnectable = (bookingType: BookingType): bookingType is IdleDisconnectableBookingType => (
  bookingType === BookingType.Lesson ||
  bookingType === BookingType.Meeting
);
